/**
 * Shared hook for infinite scroll using IntersectionObserver
 *
 * Handles the IntersectionObserver setup and cleanup for infinite loading.
 * Uses refs to avoid stale closures and ensure latest values are used.
 */

import { useRef, useEffect, type RefObject } from 'react';

export interface UseInfiniteObserverOptions {
  enabled?: boolean;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
}

export interface UseInfiniteObserverReturn {
  sentinelRef: RefObject<HTMLDivElement | null>;
}

/**
 * Hook for infinite scroll using IntersectionObserver
 */
export function useInfiniteObserver({
  enabled = true,
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '100px',
  threshold = 0.1,
}: UseInfiniteObserverOptions): UseInfiniteObserverReturn {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Firing lock to prevent double-triggering
  const firingRef = useRef(false);

  // Store latest values in refs to avoid stale closures
  const latest = useRef({
    enabled,
    hasMore,
    isLoading,
    onLoadMore,
  });

  // Update latest values when they change
  useEffect(() => {
    latest.current = {
      enabled,
      hasMore,
      isLoading,
      onLoadMore,
    };
  }, [enabled, hasMore, isLoading, onLoadMore]);

  // Reset firing lock when not loading
  useEffect(() => {
    if (!isLoading) {
      firingRef.current = false;
    }
  }, [isLoading]);

  // Create and manage the IntersectionObserver
  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const { hasMore: currentHasMore, isLoading: currentIsLoading, onLoadMore: currentOnLoadMore } = latest.current;

        if (entries[0].isIntersecting && currentHasMore && !currentIsLoading && !firingRef.current) {
          firingRef.current = true;
          currentOnLoadMore();
        }
      },
      {
        root: null,
        rootMargin,
        threshold,
      }
    );

    const currentTarget = sentinelRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      observer.disconnect();
    };
  }, [enabled, rootMargin, threshold]);

  return {
    sentinelRef,
  };
}
